import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Separator } from '../ui/separator';
import { Plus, Pencil, CheckCircle } from 'lucide-react';
import { casinoIssuesAPI } from '../../services/api';

const ISSUE_TYPES = ['login', 'game', 'deposit', 'freespins', 'cashout', 'other'];
const TYPE_COLORS = { login: '#E21351', game: '#6F00FF', deposit: '#3861FB', freespins: '#00BD71', cashout: '#FFD22F', other: '#9aa0ad' };

function fmtDate(iso) { if (!iso) return '—'; return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
function toLocalDatetimeValue(iso) { if (!iso) return ''; const d = new Date(iso); const pad = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; }

const EMPTY_FORM = { type: 'other', description: '', bot_response: '', start_at: toLocalDatetimeValue(new Date().toISOString()), end_at: '', after_resolve_text: '', after_resolve_hours: '' };

function IssueRow({ issue, onEdit, onClose }) {
  const color = TYPE_COLORS[issue.type] || '#9aa0ad';
  const isOpen = !issue.end_at;
  return (
    <div className="flex items-start gap-3 p-3 last:border-b-0 border-b border-white/5" style={{ borderLeft: `3px solid ${isOpen ? color : '#262c3d'}`, background: isOpen ? color + '08' : 'transparent' }}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ background: color + '22', color }}>{issue.type}</span>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: isOpen ? '#E2135122' : '#262c3d', color: isOpen ? '#E21351' : '#9aa0ad' }}>{isOpen ? 'OPEN' : 'CLOSED'}</span>
          <span className="text-[11px] text-ink-dim ml-auto">Started: {fmtDate(issue.start_at)}{issue.end_at && <> · Closed: {fmtDate(issue.end_at)}</>}</span>
        </div>
        <p className="text-[13px] text-ink">{issue.description}</p>
      </div>
      <div className="flex gap-1 shrink-0">
        <Tooltip><TooltipTrigger asChild><button onClick={() => onEdit(issue)} className="p-1 text-ink-dim hover:text-brand-yellow rounded-md"><Pencil className="h-4 w-4" /></button></TooltipTrigger><TooltipContent>Edit</TooltipContent></Tooltip>
        {isOpen && <Tooltip><TooltipTrigger asChild><button onClick={() => onClose(issue)} className="p-1 text-ink-dim hover:text-brand-green rounded-md"><CheckCircle className="h-4 w-4" /></button></TooltipTrigger><TooltipContent>Close issue</TooltipContent></Tooltip>}
      </div>
    </div>
  );
}

function IssueFormDialog({ open, onClose, onSave, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showAfterResolve, setShowAfterResolve] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (open) { setSubmitted(false); setSaveError(''); setShowAfterResolve(!!(initial?.after_resolve_text)); setForm(initial ? { type: initial.type, description: initial.description, bot_response: initial.bot_response || '', start_at: toLocalDatetimeValue(initial.start_at), end_at: toLocalDatetimeValue(initial.end_at), after_resolve_text: initial.after_resolve_text || '', after_resolve_hours: initial.after_resolve_hours != null ? String(initial.after_resolve_hours) : '' } : EMPTY_FORM); }
  }, [open, initial]);

  const handleSave = async () => {
    setSubmitted(true); if (!form.type || !form.description.trim() || !form.bot_response.trim() || !form.start_at) return;
    setSaving(true); setSaveError('');
    try {
      const body = { type: form.type, description: form.description.trim(), bot_response: form.bot_response.trim(), start_at: new Date(form.start_at).toISOString() };
      if (showAfterResolve && form.after_resolve_text.trim()) { body.after_resolve_text = form.after_resolve_text.trim(); body.after_resolve_hours = form.after_resolve_hours ? parseInt(form.after_resolve_hours, 10) : null; } else if (isEdit && !showAfterResolve) { body.clear_after_resolve = true; }
      if (isEdit) { body.end_at = form.end_at ? new Date(form.end_at).toISOString() : null; body.clear_end_at = !form.end_at; await casinoIssuesAPI.update(initial.id, body); } else { await casinoIssuesAPI.create(body); }
      onSave();
    } catch (err) { setSaveError(err?.response?.data?.error || 'Failed to save issue.'); } finally { setSaving(false); }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const canSave = !saving && form.description.trim() && form.bot_response.trim() && form.start_at && !(showAfterResolve && (!form.after_resolve_text.trim() || !form.after_resolve_hours));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Issue' : 'New Casino Issue'}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <div><Select value={form.type} onValueChange={v => set('type', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ISSUE_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent></Select></div>
          <div>
            <label className="text-xs text-ink-dim mb-1 block">Description *</label>
            <Textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What happened, where it occurs, and how to recognize it." />
            {submitted && !form.description.trim() && <p className="text-[11px] text-negative mt-0.5">Description is required</p>}
          </div>
          <div>
            <label className="text-xs text-ink-dim mb-1 block">Bot response *</label>
            <Textarea rows={3} value={form.bot_response} onChange={e => set('bot_response', e.target.value)} placeholder="What the bot should tell the player." />
            {submitted && !form.bot_response.trim() && <p className="text-[11px] text-negative mt-0.5">Bot response is required</p>}
          </div>
          <Separator />
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAfterResolve(!showAfterResolve)} className={`w-9 h-5 rounded-full transition-colors ${showAfterResolve ? 'bg-brand-yellow' : 'bg-surface-3'}`}>
              <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${showAfterResolve ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
            <span className="text-xs text-ink-dim">After resolve message</span>
          </div>
          {showAfterResolve && <>
            <div><label className="text-xs text-ink-dim mb-1 block">After resolve text *</label><Textarea rows={2} value={form.after_resolve_text} onChange={e => set('after_resolve_text', e.target.value)} placeholder="Message after issue is resolved." /></div>
            <div><label className="text-xs text-ink-dim mb-1 block">Show for (hours) *</label><Input type="number" min={1} value={form.after_resolve_hours} onChange={e => set('after_resolve_hours', e.target.value)} placeholder="e.g. 24" /></div>
          </>}
          <div><label className="text-xs text-ink-dim mb-1 block">Start time</label><Input type="datetime-local" value={form.start_at} onChange={e => set('start_at', e.target.value)} /></div>
          {isEdit && <div><label className="text-xs text-ink-dim mb-1 block">End time (leave empty if still open)</label><Input type="datetime-local" value={form.end_at} onChange={e => set('end_at', e.target.value)} /></div>}
        </div>
        {saveError && <Alert variant="error" className="mb-2"><AlertDescription>{saveError}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={!canSave}>{saving ? <Spinner size="sm" /> : (isEdit ? 'Save' : 'Create')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PAGE_SIZE = 25;

export default function CasinoIssuesPage() {
  const [issues, setIssues] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [pageError, setPageError] = useState('');

  const fetchIssues = useCallback(async () => {
    setLoading(true); setPageError('');
    try { const res = await casinoIssuesAPI.list({ active_only: activeOnly, page, page_size: PAGE_SIZE }); setIssues(res.data.issues || []); setTotal(res.data.total || 0); }
    catch (err) { setPageError(err?.response?.data?.error || 'Failed to load issues.'); }
    finally { setLoading(false); }
  }, [activeOnly, page]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);
  useEffect(() => { setPage(1); }, [activeOnly]);

  const handleEdit = (issue) => { setEditTarget(issue); setDialogOpen(true); };
  const handleClose = async (issue) => { try { await casinoIssuesAPI.update(issue.id, { end_at: new Date().toISOString() }); fetchIssues(); } catch (err) { setPageError(err?.response?.data?.error || 'Failed to close issue.'); } };
  const handleSave = () => { setDialogOpen(false); setEditTarget(null); fetchIssues(); };

  const openCount = activeOnly ? total : issues.filter(i => !i.end_at).length;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="max-w-[860px] mx-auto">
      {pageError && <Alert variant="error" className="mb-4"><AlertDescription>{pageError}</AlertDescription></Alert>}
      <div className="flex items-center justify-between mb-4">
        <div><h2 className="text-lg font-bold text-ink">Casino Issues</h2>{openCount > 0 && <p className="text-xs text-negative mt-0.5">{openCount} open {openCount === 1 ? 'issue' : 'issues'}</p>}</div>
        <div className="flex gap-2 items-center">
          <button onClick={() => setActiveOnly(v => !v)} className={`px-3 py-1 text-xs rounded-md border cursor-pointer transition-colors ${activeOnly ? 'border-brand-yellow text-brand-yellow bg-brand-yellow/10' : 'border-white/10 text-ink-dim'}`}>Open only</button>
          <Button size="sm" onClick={() => { setEditTarget(null); setDialogOpen(true); }} className="gap-1.5"><Plus className="h-3.5 w-3.5" />New Issue</Button>
        </div>
      </div>
      <div className="bg-surface-1 border border-white/10 rounded-lg overflow-hidden">
        {loading ? <div className="flex justify-center py-8"><Spinner size="sm" /></div> : issues.length === 0 ? <div className="py-8 text-center text-sm text-ink-dim">{activeOnly ? 'No open issues' : 'No issues found'}</div> : issues.map(issue => <IssueRow key={issue.id} issue={issue} onEdit={handleEdit} onClose={handleClose} />)}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-[11px] text-ink-dim">{total} total</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
            <span className="text-[11px] text-ink-dim">{page} / {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</Button>
          </div>
        </div>
      )}
      <IssueFormDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditTarget(null); }} onSave={handleSave} initial={editTarget} />
    </div>
  );
}
